import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  TablePagination,
  Skeleton,
  alpha,
  Link,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Launch as LaunchIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { ColumnConfig, FlattenedFeedbackResponse, formatDateEnhanced } from '../../utils/customerSentimentutils';
import { CustomerRequestResponse } from '../../services/api';
import { TableHeader } from './TableHeader';
import { TypeChip } from './TypeChip';
import { normalizeZohoIssueLinkUrl, zohoIssueLinkProps } from './zohoIssueUrl';
import { useMuiTableColumnResize } from '../../hooks/useMuiTableColumnResize';
import { CUSTOMER_SENTIMENT_DEFAULT_COLUMN_PX } from './customerSentimentColumnWidths';

export interface FeedbackResponsesTableProps {
  columns: ColumnConfig[];
  data: FlattenedFeedbackResponse[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSort: (columnId: string) => void;
  onFilterClick: (columnId: string, event: React.MouseEvent<HTMLElement>) => void;
  isFilterActive: (columnId: string) => boolean;
  renderFilterMenu: (columnId: string, columnLabel: string) => React.ReactNode;
  feedbackRequests: CustomerRequestResponse[];
  onViewClick: (request: CustomerRequestResponse) => void;
  onRetryZoho?: (parentRequestId: string, feedbackResponseId: string) => void;
  retryingZohoKey?: string | null;
}

export const FeedbackResponsesTable: React.FC<FeedbackResponsesTableProps> = ({
  columns,
  data,
  loading,
  error,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onFilterClick,
  isFilterActive,
  renderFilterMenu,
  feedbackRequests,
  onViewClick,
  onRetryZoho,
  retryingZohoKey,
}) => {
  const colResize = useMuiTableColumnResize(CUSTOMER_SENTIMENT_DEFAULT_COLUMN_PX);
  const getColumnMinWidth = (columnId: string): string => {
    switch (columnId) {
      case 'requestNumber':
        return '160px';
      case 'date':
        return '112px';
      case 'projectCode':
        return '120px';
      case 'projectName':
        return '180px';
      case 'projectOwner':
        return '140px';
      case 'customerName':
        return '140px';
      case 'customerEmail':
        return '180px';
      case 'account':
        return '150px';
      case 'region':
        return '100px';
      case 'overallSatisfaction':
        return '160px';
      case 'deliveryCompletion':
      case 'deliveryTimeline':
      case 'deliveryClosure':
      case 'teamTechnical':
      case 'teamSupport':
      case 'teamResponsiveness':
        return '120px';
      case 'specificContributions':
      case 'areasForImprovement':
        return '200px';
      case 'zohoStatus':
        return '120px';
      case 'zohoIssueLink':
        return '140px';
      default:
        return '104px';
    }
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible', width: '100%' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed', minWidth: 800 }}>
          <TableHead>
            <TableHeader
              columns={columns}
              onSort={onSort}
              onFilterClick={onFilterClick}
              isFilterActive={isFilterActive}
              renderFilterMenu={renderFilterMenu}
              getColumnMinWidth={getColumnMinWidth}
              getColumnWidthPx={colResize.getWidth}
              onBeginColumnResize={colResize.beginResize}
              resizingColumnId={colResize.resizingColumnId}
            />
          </TableHead>
          <TableBody>
          {loading ? (
            [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {Array.from({ length: columns.length }).map((_, cellIndex) => (
                  <TableCell key={cellIndex} align="center" sx={{ py: 1, fontSize: '0.875rem' }}>
                    <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : error ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography color="error">{error}</Typography>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary">
                  No feedback responses found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((response, index) => {
              const responseData = response as FlattenedFeedbackResponse;
              return (
                <TableRow
                  key={`${responseData.responseId}-${index}`}
                  sx={{
                    '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.02) },
                    '&:nth-of-type(even)': { backgroundColor: alpha(SEARCE_COLORS.neutral_100, 0.5) },
                  }}
                >
                  {columns.map((column) => {
                    const value = column.field(responseData);
                    const displayValue = column.isDate 
                      ? formatDateEnhanced(value) 
                      : (value || '-');

                    // Request ID as link (opens detail modal)
                    if (column.id === 'requestNumber') {
                      const request = feedbackRequests.find((r) => r.request_number === responseData.requestNumber);
                      return (
                        <TableCell
                          key={column.id}
                          align="center"
                          sx={{
                            color: SEARCE_COLORS.textPrimary,
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            py: 1,
                            ...colResize.colWidthSx(column.id),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            position: 'relative',
                            ...(request && {
                              cursor: 'pointer',
                              color: SEARCE_COLORS.primary,
                              fontWeight: 500,
                              '&:hover': { textDecoration: 'underline' },
                            }),
                          }}
                        >
                          {request ? (
                            <Typography
                              component="span"
                              onClick={() => onViewClick(request)}
                              sx={{
                                color: 'inherit',
                                fontWeight: 'inherit',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {displayValue}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {displayValue}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    }

                    // Special rendering for specific columns
                    if (column.id === 'overallSatisfaction' && value) {
                      return (
                        <TableCell
                          key={column.id}
                          align="center"
                          sx={{
                            color: SEARCE_COLORS.textPrimary,
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            py: 1,
                            ...colResize.colWidthSx(column.id),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <TypeChip type={String(value)} />
                          </Box>
                        </TableCell>
                      );
                    }

                    if (column.id === 'zohoStatus' && value) {
                      const zohoStatus = String(value);
                      const parentId = responseData.parentRequestId;
                      const respId = responseData.responseId;
                      const rk = `${parentId}:feedback_response:${respId}`;
                      const showRetry =
                        zohoStatus.includes('Failed') &&
                        Boolean(onRetryZoho) &&
                        Boolean(parentId) &&
                        Boolean(respId);
                      const isRetrying = retryingZohoKey === rk;
                      return (
                        <TableCell
                          key={column.id}
                          align="center"
                          sx={{
                            color: SEARCE_COLORS.textPrimary,
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            py: 1,
                            ...colResize.colWidthSx(column.id),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.75,
                              flexWrap: 'wrap',
                            }}
                          >
                            <TypeChip type={zohoStatus} />
                            {showRetry && (
                              <Tooltip title={isRetrying ? 'Retrying…' : 'Retry Zoho issue'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isRetrying}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (parentId && respId) onRetryZoho?.(parentId, respId);
                                    }}
                                    sx={{ padding: '4px', color: SEARCE_COLORS.primary }}
                                    aria-label="Retry Zoho issue"
                                  >
                                    {isRetrying ? (
                                      <CircularProgress size={20} thickness={4} sx={{ color: SEARCE_COLORS.primary }} />
                                    ) : (
                                      <ReplayIcon sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      );
                    }

                    if (column.id === 'zohoIssueLink' && value) {
                      const href = normalizeZohoIssueLinkUrl(String(value));
                      return (
                        <TableCell
                          key={column.id}
                          align="center"
                          sx={{
                            color: SEARCE_COLORS.textPrimary,
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            py: 1,
                            ...colResize.colWidthSx(column.id),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Link
                            {...zohoIssueLinkProps}
                            href={href}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: SEARCE_COLORS.primary,
                              textDecoration: 'none',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            View in Zoho
                            <LaunchIcon sx={{ fontSize: 14 }} />
                          </Link>
                        </TableCell>
                      );
                    }

                    // Text columns with truncation
                    const shouldTruncate = ['requestNumber', 'projectName', 'projectOwner', 'customerName', 'customerEmail', 'account', 'specificContributions', 'areasForImprovement'].includes(column.id);
                    const isRatingColumn = ['deliveryCompletion', 'deliveryTimeline', 'deliveryClosure', 'teamTechnical', 'teamSupport', 'teamResponsiveness'].includes(column.id);
                    
                    return (
                      <TableCell
                        key={column.id}
                        align={isRatingColumn ? 'center' : 'left'}
                        sx={{
                          color: SEARCE_COLORS.textPrimary,
                          fontWeight: isRatingColumn ? 500 : 400,
                          fontSize: '0.875rem',
                          py: 1,
                          ...colResize.colWidthSx(column.id),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          position: 'relative',
                          whiteSpace: column.id === 'date' ? 'nowrap' : (shouldTruncate ? 'nowrap' : 'normal'),
                          ...(shouldTruncate && {
                            maxWidth: column.id === 'specificContributions' || column.id === 'areasForImprovement' ? '200px' : undefined,
                          }),
                        }}
                      >
                        {shouldTruncate && value && value !== '-' ? (
                          <Tooltip title={String(value)} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'help',
                              }}
                            >
                              {displayValue}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: value === '-' ? alpha(SEARCE_COLORS.textSecondary, 0.5) : 'inherit' }}>
                            {displayValue}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: `1px solid ${SEARCE_COLORS.border}`,
          flexShrink: 0,
          '& .MuiTablePagination-toolbar': {
            px: 2,
          },
        }}
      />
    </Box>
  );
};
